import React from 'react'
import { preToCodeBlock } from 'mdx-utils'

import { Code, Heading, Text } from 'components/Text'

import { CodeBlock } from './CodeBlock'
import { Note } from './Note'
import { UserCapture } from './UserCapture'
import { Quote } from './Quote'
import { TutorialNavigation } from './TutorialNavigation'
import { ElementContainer } from './ElementContainer'

const fullWidthElements = ['pre', 'span', 'UserCapture']

const applyOnContiguousMatches = (children, test, cb) => {
  const currentGroup = []
  const result = []
  for (const child of children) {
    if (!!test(child)) {
      currentGroup.push(child)
    } else {
      if (currentGroup.length > 0) {
        /*
                   If the current child doesn't pass the test and there is currently a
                   group of passing items, add the result of the callback applied on
                   a copy of that group, then clear the group.
                 */
        result.push(cb(currentGroup.slice()))
        currentGroup.length = 0
      }
      // Add the non-passing child directly to the result, ungrouped.
      result.push(child)
    }
  }
  // Process the last group if the last child passed.
  if (currentGroup.length > 0) {
    result.push(cb(currentGroup))
  }
  return result
}

const containContiguous = (children) => {
  let lastKey = 1

  return applyOnContiguousMatches(
    children,
    (child) =>
      !fullWidthElements.includes(child.props.originalType) &&
      !fullWidthElements.includes(child.props.mdxType),
    (matches) => (
      <ElementContainer key={`contained-group-${lastKey++}`}>
        {matches}
      </ElementContainer>
    )
  )
}

export const mdxComponents = {
  h1: (props) => (
    <Heading {...props} as="h1" fontSize={8} fontWeight={800} lineHeight={10} />
  ),
  h2: (props) => (
    <Heading
      {...props}
      as="h2"
      marginTop={7}
      fontSize={[6, 6, 7]}
      fontWeight={800}
      lineHeight={[8, 8, 9]}
    />
  ),
  h3: (props) => (
    <Heading
      {...props}
      as="h3"
      marginTop={7}
      fontSize={[5, 5, 6]}
      fontWeight={800}
      lineHeight={[7, 7, 8]}
    />
  ),
  h4: (props) => (
    <Heading
      {...props}
      as="h4"
      marginTop={7}
      fontSize={6}
      fontWeight={800}
      lineHeight={8}
    />
  ),
  h5: (props) => (
    <Heading
      {...props}
      as="h5"
      marginTop={7}
      fontSize={6}
      fontWeight={800}
      lineHeight={8}
    />
  ),
  h6: (props) => (
    <Heading
      {...props}
      as="h6"
      marginTop={7}
      color="altText"
      fontSize={5}
      fontWeight={500}
      lineHeight={7}
    />
  ),
  a: (props) => (
    <Text
      {...props}
      color="primary"
      as="a"
      ontSize={[3, 3, 4]}
      lineHeight={[5, 5, 6]}
    />
  ),
  p: (props) => (
    <Text
      {...props}
      opacity={0.8}
      fontSize={[3, 3, 4]}
      fontWeight={400}
      marginY={4}
      lineHeight={[5, 5, 6]}
    />
  ),
  li: (props) => (
    <Text
      {...props}
      as="li"
      fontSize={[3, 3, 4]}
      fontWeight={400}
      marginY={2}
      lineHeight={[5, 5, 6]}
    />
  ),
  blockquote: (props) => <Quote {...props} />,
  Note,
  UserCapture,
  code: (originalProps) => {
    return <Code {...originalProps} />
  },
  pre: (originalProps) => {
    const props = preToCodeBlock(originalProps)

    if (props) {
      return <CodeBlock {...props} />
    }

    return <Code {...originalProps} />
  },
  wrapper: ({ children, ...props }) => {
    return (
      <>
        {containContiguous(children)}
        <TutorialNavigation />
      </>
    )
  },
}
